import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, BadgePercent, Users, UserPlus } from 'lucide-react';
import PersonAvatar from '../../../components/PersonAvatar';
import { linkFamily, unlinkFamily, listMembers, discountLabel, Discount, FamiliarInfo, MemberRow, PosibleFamiliar } from '../api';
import { useToast } from '../../../context/ToastContext';
import { EstadoBadge, FamiliarDetalle } from './FamiliarChip';

// PESTAÑA «FAMILIA» (23-sep-2026, Carlos: «lo de la familia o descuentos no podría ser una pestaña? …así podría
// tener más control»). Junta en un solo lugar: el descuento de la alumna (y si es de familia / está corregido aquí),
// su familia registrada con tarjeta por integrante, las posibles (mismo teléfono / mismos apellidos) y el alta
// de un familiar a mano. Registrar o quitar familia NO toca dinero: el descuento se cambia aparte, a propósito.

export interface MiembroDescuento {
  discount_id: number | null; discount_name: string | null; discount_value: number | string | null;
  discount_is_percentage: number | boolean | null; discount_expired?: boolean; discount_included?: boolean;
  discount_is_family?: boolean;
  discount_override?: { kydemy_discount_id: number; kydemy_discount_name: string | null } | null;
  active?: boolean | number;
}

const cardCls = 'rounded-xl border border-line bg-surface-2/30 p-3';
const h3Cls = 'mb-2 flex items-center gap-2 text-sm font-semibold text-content';

export default function MemberFamilia({ contactId, nombre, m, familia, posible, discounts, discountSel, onPickDiscount, puedeEditar, onCambio }: {
  contactId: number;
  nombre: string;
  m: MiembroDescuento;
  familia: FamiliarInfo[];
  posible: PosibleFamiliar[];
  discounts: Discount[];
  discountSel: string;
  onPickDiscount: (id: string) => void;
  puedeEditar: boolean;
  onCambio: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [cands, setCands] = useState<MemberRow[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) { setCands([]); return; }
    const t = setTimeout(() => listMembers({ search: q.trim(), status: 'all', page_size: 8 })
      .then((r) => setCands(r.data.filter((x) => x.account_id !== contactId && !familia.some((f) => f.contact_id === x.account_id))))
      .catch(() => setCands([])), 250);
    return () => clearTimeout(t);
  }, [q, contactId, familia]);

  async function vincular(otra: number, quien: string) {
    setSaving(true);
    try { await linkFamily(contactId, otra); toast.success(`${quien} quedó registrada como familia`); setQ(''); onCambio(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo registrar'); }
    finally { setSaving(false); }
  }
  async function quitar(f: FamiliarInfo) {
    if (!window.confirm(`¿Quitar a ${f.nombre} de la familia? No cambia ningún descuento ni cobro.`)) return;
    setSaving(true);
    try { await unlinkFamily(f.contact_id); toast.success(`${f.nombre} ya no está en la familia`); onCambio(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
    finally { setSaving(false); }
  }

  const activas = familia.filter((f) => f.activa !== false);
  const esFamilia = !!m.discount_is_family && !m.discount_expired;
  const alerta = esFamilia && activas.length === 0
    ? (familia.length
      ? 'Tiene descuento de familia pero ningún familiar sigue activo: revisar si todavía le toca.'
      : 'Tiene descuento de familia pero no hay familia registrada: registra con quién comparte el descuento.')
    : null;
  const sugerencia = !m.discount_id && activas.length > 0
    ? `Tiene ${activas.length === 1 ? 'un familiar activo' : `${activas.length} familiares activos`} y no tiene descuento: ¿le toca descuento de familia?`
    : null;

  return (
    <div className="space-y-4" data-testid="tab-familia">
      {/* ── Descuento ── */}
      <section className={cardCls} data-testid="familia-descuento">
        <h3 className={h3Cls}><BadgePercent size={15} className="text-accent-ink" /> Descuento</h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {m.discount_name ? (
            <>
              <b className={m.discount_expired ? 'text-content-3 line-through' : 'text-content'}>{m.discount_name}</b>
              <span className="rounded bg-brand-blue/10 px-1.5 py-0.5 text-xs font-medium text-brand-blue">{discountLabel(m.discount_value, !!m.discount_is_percentage)}</span>
              {m.discount_is_family && <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-300">de familia</span>}
              {m.discount_included && <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px] text-content-3">ya incluido en el precio del plan</span>}
              {m.discount_expired && <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] font-medium text-red-600 dark:text-red-400">vencido</span>}
            </>
          ) : <span className="text-content-3">Sin descuento</span>}
        </div>
        {m.discount_override && (
          <p className="mt-1.5 text-xs text-content-3" data-testid="descuento-corregido">
            ✏️ Corregido aquí. En Kydemy dice «{m.discount_override.kydemy_discount_name || `#${m.discount_override.kydemy_discount_id}`}»; la sincronización diaria respeta este cambio.
          </p>
        )}
        {alerta && <p className="mt-2 rounded-lg bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-300" data-testid="descuento-alerta">⚠️ {alerta}</p>}
        {sugerencia && <p className="mt-2 rounded-lg bg-brand-blue/5 px-2.5 py-1.5 text-xs text-content-2">💡 {sugerencia}</p>}
        {puedeEditar && (
          <div className="mt-2 max-w-sm">
            <select value={discountSel} onChange={(e) => onPickDiscount(e.target.value)} data-testid="familia-descuento-select"
              className="w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-content outline-none focus:border-brand-blue">
              <option value="">Sin descuento</option>
              {discounts.map((d) => (
                <option key={d.discount_id} value={String(d.discount_id)}>
                  {d.is_family ? '👪 ' : ''}{d.name} ({discountLabel(d.value, d.is_percentage)}){d.deleted_at ? ' · archivado' : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-content-3">Se guarda al elegir. Cambia lo que se le sugiere al cobrar desde ahora; los pagos ya hechos no se tocan.</p>
          </div>
        )}
      </section>

      {/* ── Familia registrada ── */}
      <section className={cardCls} data-testid="familia-registrada">
        <h3 className={h3Cls}><Users size={15} className="text-accent-ink" /> Familia registrada {familia.length > 0 && <span className="font-normal text-content-3">· {familia.length + 1} integrantes con {nombre.split(' ')[0]}</span>}</h3>
        {familia.length === 0 ? (
          <p className="text-xs text-content-3">No tiene familia registrada.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {familia.map((f) => (
              <div key={f.contact_id} className="rounded-lg border border-line bg-surface p-3" data-testid="familiar-card">
                <div className="mb-2 flex items-center gap-2.5">
                  <PersonAvatar contactId={f.contact_id} hasPhoto={f.has_photo} name={f.first_name || f.nombre} lastName={f.last_name} size="h-10 w-10" initialClass="text-sm" />
                  <div className="min-w-0 flex-1">
                    <Link to={`/alumnos/${f.contact_id}`} className="block truncate text-sm font-semibold text-content hover:text-brand-blue hover:underline">{f.nombre}</Link>
                    <EstadoBadge activa={f.activa} />
                  </div>
                </div>
                <FamiliarDetalle f={f} />
                <div className="mt-2 flex items-center justify-between">
                  <Link to={`/alumnos/${f.contact_id}`} className="text-xs font-medium text-brand-blue hover:underline">Ir al perfil →</Link>
                  {puedeEditar && (
                    <button type="button" disabled={saving} onClick={() => quitar(f)} className="text-xs text-content-3 underline hover:text-red-600">Quitar de la familia</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Posible familia ── */}
      {posible.length > 0 && (
        <section className={cardCls} data-testid="familia-posible">
          <h3 className={h3Cls}>👪 Posible familia <span className="font-normal text-content-3">· mismo teléfono o mismos apellidos</span></h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {posible.map((f) => (
              <div key={f.contact_id} className="rounded-lg border border-dashed border-line bg-surface p-3">
                <div className="mb-2 flex items-center gap-2.5">
                  <PersonAvatar contactId={f.contact_id} hasPhoto={f.has_photo} name={f.first_name || f.nombre} lastName={f.last_name} size="h-10 w-10" initialClass="text-sm" />
                  <div className="min-w-0 flex-1">
                    <Link to={`/alumnos/${f.contact_id}`} className="block truncate text-sm font-semibold text-content hover:underline">{f.nombre}</Link>
                    <span className="text-[11px] text-content-3">{f.razones.join(' y ')}</span>
                  </div>
                </div>
                <FamiliarDetalle f={f} />
                {puedeEditar && (
                  <button type="button" disabled={saving} onClick={() => vincular(f.contact_id, f.nombre)}
                    className="mt-2 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-content-2 hover:border-brand-blue hover:text-content">Es familia</button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Registrar familiar a mano ── */}
      {puedeEditar && (
        <section className={cardCls}>
          <h3 className={h3Cls}><UserPlus size={15} className="text-accent-ink" /> Registrar familiar</h3>
          <div className="relative max-w-md">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-3" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar alumna por nombre…" data-testid="familia-buscar"
              className="w-full rounded-lg border border-line bg-surface py-2 pl-8 pr-8 text-sm text-content outline-none focus:border-brand-blue" />
            {q && <button type="button" onClick={() => setQ('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-content-3" aria-label="Limpiar"><X size={14} /></button>}
            {cands.length > 0 && (
              <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
                {cands.map((c) => {
                  const nm = `${c.firstname} ${c.lastname || ''}`.trim();
                  return (
                    <button key={c.account_id} type="button" disabled={saving} onClick={() => vincular(c.account_id, nm)}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-content hover:bg-surface-2">
                      <PersonAvatar contactId={c.account_id} hasPhoto={(c as any).has_photo} name={c.firstname} lastName={c.lastname} size="h-6 w-6" initialClass="text-[10px]" />
                      <span className="flex-1">{nm}</span>
                      {(c as any).active === 0 || (c as any).active === false ? <EstadoBadge activa={false} /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <p className="mt-1.5 text-[11px] text-content-3">Registrar la familia no cambia descuentos: sirve para revisar cada mes quién tiene descuento de familia sin ningún familiar activo.</p>
        </section>
      )}
    </div>
  );
}
